<template>
  <layout width="428">
    <template slot="header">
      {{ isNew ? 'Add new' : 'Edit' }} control
    </template>
    <validation-observer ref="form">
      <v-form class="mx-4 mt-2" :disabled="isLoading">
        <validation-provider v-slot="{ errors }" name="Object" rules="required">
          <v-autocomplete
            clearable
            data-test="object"
            v-model="objectId"
            :items="linkedObjects"
            item-text="name"
            item-value="id"
            :error-messages="errors"
            label="Object"
            class="ma-0"
          >
            <template v-slot:item="item">
              <div :data-test-object="item.item.name">
                {{ item.item.name }}
              </div>
            </template>
          </v-autocomplete>
        </validation-provider>
        <!--        <common-schema-selector v-model="schemaId" />-->
        <validation-provider
          v-slot="{ errors }"
          name="Control"
          rules="required"
        >
          <v-autocomplete
            clearable
            data-test="control"
            :error-messages="errors"
            v-model="rpc"
            :disabled="!Boolean(objectId)"
            :items="groupControls"
            item-text="title"
            item-value="value"
            label="Control"
            class="ma-0"
          >
            <template v-slot:item="item">
              <div :data-test-control="item.item.title">
                {{ item.item.title }}
              </div>
            </template>
          </v-autocomplete>
        </validation-provider>
        <template v-if="listArguments.length">
          <subheader>Parameters</subheader>
          <control-parameter-field
            v-model="parameters"
            :properties="listArguments"
            v-if="listArguments.length"
          />
        </template>
        <subheader>Execution</subheader>
        <div class="d-flex align-center mx-0">
          <div style="font-size: 16px">
            Wait for RPC to finish (sync execution)
          </div>
          <v-spacer />
          <v-switch
            v-model="execution.syncExecution"
            class="ma-0 py-3"
            data-test-switch="syncExecution"
            hide-details
          ></v-switch>
        </div>
        <div class="d-flex align-center mx-0">
          <div style="font-size: 16px">Stop schedule execution on failure</div>
          <v-spacer />
          <v-switch
            v-model="execution.stopOnFailure"
            class="ma-0 py-3"
            data-test-switch="stopOnFailure"
            hide-details
          ></v-switch>
        </div>
        <v-text-field
          min="0"
          type="number"
          v-model="execution.pauseAfterExecution"
          data-test-field="pauseAfterExecution"
          label="Pause after execution (seconds)"
          clearable
        ></v-text-field>
      </v-form>
    </validation-observer>
    <v-divider />
    <template #footer>
      <v-spacer />
      <v-btn text @click="close" data-test-action="closeEditControlAction">
        cancel
      </v-btn>
      <v-btn
        color="primary"
        data-test-action="createEditControlAction"
        :loading="isLoading"
        text
        @click.stop="handleSave"
      >
        <template v-if="isNew">add</template>
        <template v-else>save</template>
      </v-btn>
    </template>
  </layout>
</template>

<script>
import Layout from '@/layouts/LayoutPopup';

import {
  computed,
  ref,
  onMounted,
  reactive,
  toRefs,
  watch
} from '@vue/composition-api';

import { required } from 'vee-validate/dist/rules';
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';

extend('required', {
  ...required,
  message: 'This field is required'
});
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
  useResult
} from '@vue/apollo-composable';

import gql from '@/api/objects/item.graphql';
import { nanoid } from 'nanoid';
import cloneDeep from 'lodash.clonedeep';
import schedulesSchema from '@/api/schedules/item.graphql';
import ControlParameterField from '@/components/ControlParameterField.vue';

export default {
  name: 'AddControlModal',
  props: {
    linkedObjects: {
      type: Array,
      default: () => []
    },
    targetProperty: {
      type: Object,
      default: () => ({})
    },
    entity: {
      type: Object,
      default: () => ({})
    }
  },
  components: {
    ControlParameterField,
    Layout,
    ValidationObserver,
    ValidationProvider
  },

  setup(props, { emit }) {
    const { resolveClient } = useApolloClient();
    const { mutate: updateProperty } = useMutation(gql.updateObjectProperty);

    const form = ref(null);

    const localState = reactive({
      name: '',
      // schemaId: null,
      // schemaType: 'device',
      objectId: null,
      rpc: null,
      parameters: [],
      execution: {
        stopOnFailure: false,
        syncExecution: false,
        pauseAfterExecution: 0
      }
    });

    // const lazyQuery = useLazyQuery(
    //   schedulesSchema.loadSchemaWithControls,
    //   { id: null },
    //   {
    //     fetchPolicy: 'network-only',
    //     nextFetchPolicy: 'network-only'
    //   }
    // );

    const isNew = computed(() => {
      return !props.entity.id;
    });

    function close() {
      emit('close');
    }

    const createControlName = () => {
      const object = props.linkedObjects.find(
        item => item.id === localState.objectId
      );

      const rpc = groupControls.value.find(
        item => item.value === localState.rpc
      );

      return `${object.name}: ${rpc.title}`;
    };

    const createObjectHandler = () => {
      const payload = {
        id: nanoid(),
        name: createControlName(),
        objectId: localState.objectId,
        // schemaId: localState.schemaId,
        // schemaType: localState.schemaType,
        rpc: localState.rpc,
        parameters: cloneDeep(localState.parameters),
        execution: cloneDeep(localState.execution)
      };

      updateProperty({
        input: {
          id: props.targetProperty.id,
          patch: {
            value: [...props.targetProperty.value, payload]
          }
        }
      }).then(() => {
        close();
      });
    };

    const updateObjectHandler = () => {
      let value = cloneDeep(props.targetProperty.value);

      const targetIndex = value.findIndex(item => item.id === props.entity.id);

      const payload = {
        id: props.entity.id,
        name: createControlName(),
        objectId: localState.objectId,
        // schemaId: localState.schemaId,
        // schemaType: localState.schemaType,
        rpc: localState.rpc,
        parameters: localState.parameters,
        execution: localState.execution
      };

      value.splice(targetIndex, 1, payload);

      updateProperty({
        input: {
          id: props.targetProperty.id,
          patch: {
            value: value
          }
        }
      }).then(() => {
        close();
      });
    };

    onMounted(() => {
      if (props.entity.id) {
        const targetFilter = props.targetProperty.value.find(
          item => item.id === props.entity.id
        );
        localState.name = targetFilter.name;
        // localState.schemaId = targetFilter.schemaId;
        // localState.schemaType = targetFilter.schemaType;
        localState.objectId = targetFilter.objectId;
        localState.rpc = targetFilter.rpc;
        localState.execution = cloneDeep(targetFilter.execution);
        localState.parameters = cloneDeep(targetFilter.parameters);
      }
    });

    const handleSave = () => {
      form.value.validate().then(success => {
        if (!success) {
          return;
        }

        if (isNew.value) {
          createObjectHandler();
        } else {
          updateObjectHandler();
        }
      });
    };

    // const controls = useResult(lazyQuery.result, [], data => {
    //   return data.schema.schemaControls;
    // });

    const controls = computed(() => {
      if (!localState.objectId) {
        return [];
      }

      return (
        props.linkedObjects.find(item => item.id === localState.objectId)
          ?.schema?.schemaControls || []
      );
    });

    const groupControls = computed(() => {
      return controls.value
        .filter(item => item.rpc === item.argument)
        .map(item => ({
          title: item.description || item.rpc,
          value: item.rpc
        }));
    });

    const listArguments = computed(() => {
      if (!localState.rpc) {
        return [];
      }

      const getValue = argument => {
        const value = localState.parameters.find(item => item[argument])?.[
          argument
        ];

        if (value === undefined) {
          return null;
        }

        return value;
      };

      return controls.value
        .filter(item => item.rpc !== item.argument)
        .filter(itemInner => itemInner.rpc === localState.rpc)
        .map(item => ({
          ...item,
          title: item.description || item.rpc,
          value: getValue(item.argument)
        }));
    });

    // watch(
    //   () => localState.schemaId,
    //   () => {
    //     if (localState.schemaId) {
    //       lazyQuery.variables.value.id = localState.schemaId;
    //       lazyQuery.load();
    //     }
    //   }
    // );

    return {
      ...toRefs(localState),
      isNew,
      close,
      handleSave,
      isLoading: false,
      form,
      groupControls,
      listArguments
    };
  }
};
</script>
